import { AsyncPipe, NgIf } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthorizationService, ConfirmationDialogService } from '@myapp/modules/fwcore/services';
import {  GridModule } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { Subject } from 'rxjs';
import { ControlResizerOptions, ControlResizerService } from '../../../../../../services/framework';
import { NullableBooleanClassDirective } from '../../../../../modules/fwcore/directives/nullable-boolean-class.directive';
import { DatePreferencePipe } from '../../../../../modules/fwcore/pipes/date-preference.pipe';
import { RequestUpdateUser, ResponseUpdateUser, UserStateChecked } from '../../../models';
import { RequestUserState } from '../../../models/messages';
import { AdminService } from '../../../services';
import { UserStateGridService } from '../../../services/grids';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-user-state',
  templateUrl: './user-state.component.html',
  styleUrls: ['./user-state.component.scss'],
  standalone: true,
  imports: [GridModule, NullableBooleanClassDirective, NgIf, AsyncPipe, DatePreferencePipe, InputsModule]
})

export class UserStateComponent implements OnInit, OnDestroy {

  /*-------------------------- Input / Outputs ------------------------*/

  @Input() public companyId: string | null = null;
  @Input() public licenseId: string | null = null;


  @Input()
  set assigned(value: boolean | null) {
    this.req.assigned = value;

    if (this.viewInit) {
      this.getData();
    }
  }
  get assigned(): boolean | null {
    return this.req.assigned ?? null;
  }

  @Input()
  set active(value: boolean | null) {
    this.req.active = value;

    if (this.viewInit) {
      this.getData();
    }
  }
  get active(): boolean | null {
    return this.req.active ?? null;
  }

  @Input()
  set startsWith(value: string) {

    this.startsWithSearch = value;

    if (this.viewInit) {

      if (value && value.length > 2) {
        // Only update 
        this.req.startsWith = value;
        this.getData();
      }
      else if (value && value.length > 0) {
        this.req.startsWith = null;
      }
      else {
        this.req.startsWith = null;
        this.getData();
      }
    }

  }

  get startsWith(): string {
    return this.startsWithSearch;
  }

  @Input()
  set checkedIds(ids: string[]) {
    this.dataItems.forEach((item) => {
      item.checked = ids.includes(item.id!);
    });
  }

  get checkedIds(): string[] {

    const checkedItemIds: string[] = this.gridService.value.data
      .filter((item) => item.checked)
      .map((item) => item.id!)

    return checkedItemIds ?? [];
  }

  /*-------------------------- public properties / methods ------------*/

  public heightChange(options?: ControlResizerOptions): void {

    let ctrlOptions: ControlResizerOptions = new ControlResizerOptions();
    ctrlOptions.elemTop = this.elem.nativeElement.getBoundingClientRect().top;

    if (options) {
      ctrlOptions = { ...options, elemTop: ctrlOptions.elemTop };
    }

    const h = this.controlResizer.calculateHeight(ctrlOptions);
    this.heightGrid = h;

  }

  /*-------------------------- Internal properties  -------------------*/

  protected dataItems: UserStateChecked[] = [];

  protected heightGrid!: number;

  protected destroy$ = new Subject();

  /*-- State --*/

  public viewInit: boolean = false;

  protected loading: boolean = true;

  protected state: State = {
    skip: 0
    , take: 15
    , sort: []
  }

  protected req: RequestUserState = {
    skip: 0
    , take: 15
    , assigned: null
    , active: null
    , startsWith: null
  };

  protected startsWithSearch: string = "";

  /*--------------------------- Constructor  --------------------------*/

  constructor(protected gridService: UserStateGridService
    , protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected controlResizer: ControlResizerService
    , protected router: Router
    , protected elem: ElementRef
    , protected dialogConfirm: ConfirmationDialogService) {

  }

  /*--------------------------- ng  -----------------------------------*/


  ngOnInit(): void {

    this.req.companyId = this.companyId;
    this.req.licenseId = this.licenseId;
    this.req.assigned = this.assigned;
    this.req.active = this.active;

    this.getData();

    this.viewInit = true;
    this.sizeChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*--------------------------- event handlers  -----------------------*/

  @HostListener('window:resize', ['$event'])
  protected onResize(event: Event) {
    this.sizeChange();
  }

  // -------------- Grid processing ------------------------------------------------

  protected onStateChange(state: State): void {

    this.state = state;

    this.req.skip = this.state.skip;
    this.req.take = this.state.take;

    this.getData();
  }

  /*--------------------------- internal functions  -------------------*/

  protected sizeChange() {

    if (this.viewInit) {
      var options: ControlResizerOptions = new ControlResizerOptions();

      this.heightGrid = this.controlResizer.calculateHeight(options);
    }
  }

  protected getData() {

    this.gridService.query(this.req);
  }

  protected onAssignedChange(dataItem: any, event: boolean) {
    const checked = event;

    const model: RequestUpdateUser = {
      LicenseId: this.licenseId,
      Assigned: checked,
      UserId: dataItem.id
    }

    this.dialogConfirm.open('User Assgn/UnAssign License', `Are you sure you want to ${checked ? 'Assign' : 'Unassign'} License to this user?`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result.value) {
          this.adminService.setData$<RequestUpdateUser, ResponseUpdateUser>(model, this.adminService.updateUserAssigned)
            .pipe(takeUntil(this.destroy$))
            .subscribe((resp) => {
              if (resp.success) {
                this.getData();
              }
            });
        }
      });
  }
  protected onActiveChange(dataItem: any, event: boolean) {
    const checked = event;

    const model: RequestUpdateUser = {
      Active: checked,
      UserId: dataItem.id
    }

    this.dialogConfirm.open('User Activate/Deactivate', `Are you sure you want to ${checked ? 'activate' : 'deactivate'} this user?`)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result.value) {
          this.adminService.setData$<RequestUpdateUser, ResponseUpdateUser>(model, this.adminService.updateUserActive)
            .pipe(takeUntil(this.destroy$))
            .subscribe((resp) => {
              if (resp.success) {
                this.getData();
              }
            });
        }
      });
  }
}

